import { CalendarIcon } from "@/Icons/v2/DatePickerIcons/Calendar";
import { useTerminalsState } from "@/States/Terminals";
import { trpc } from "@/Utils/trpc";
import { defaultTimeFilter } from "@/components/v2/DatePicker/constants";
import { formatRangeLabel } from "@/components/v2/DatePicker/utils";
import { ViewWithLoader } from "@/components/v2/ViewWithLoader";
import { ContaminationPageContext } from "@/pages/contamination/context";
import { useTrans } from "@/translations";

import { useContext, useMemo } from "react";

import { getPotentialRecyclingRate } from "../utils";

export const RecoverableRateCard = () => {
  const { t } = useTrans();
  const { timeFilter, datePreset, dateRange, selectedTenantCategoryIds } =
    useContext(ContaminationPageContext);
  const { currentTerminal } = useTerminalsState();

  const { data: currentData, isLoading: isLoadingCurrentData } =
    trpc.dwh.terminals.getById.useQuery(
      {
        ...(timeFilter || defaultTimeFilter),
        terminalId: currentTerminal?.id || "",
        tenantCategories: selectedTenantCategoryIds || [],
      },
      {
        queryKeyHashFn: () =>
          [
            "terminal",
            currentTerminal?.id,
            timeFilter?.startTimeISO,
            timeFilter?.endTimeISO,
            selectedTenantCategoryIds,
          ].toString(),
        enabled: !!currentTerminal && !!timeFilter,
      },
    );

  const currentPotentialValue = useMemo(
    () => getPotentialRecyclingRate(currentData?.terminal),
    [currentData],
  );

  const currentActualValue = useMemo(
    () => +((currentData?.terminal.sortingDegree || 0) * 100).toFixed(1),
    [currentData],
  );

  const recoverableAmount = useMemo(
    () => +(currentPotentialValue - currentActualValue).toFixed(1),
    [currentPotentialValue, currentActualValue],
  );

  const displayPeriod = useMemo(
    () => formatRangeLabel(datePreset, dateRange),
    [datePreset, dateRange],
  );

  return (
    <div className="border-grey2 bg-grey1 flex w-[300px] flex-none flex-col self-center rounded-[10px] border p-[22px]">
      <ViewWithLoader isLoading={isLoadingCurrentData}>
        <span className="inline-flex w-fit items-center gap-[6px] rounded-[5px] bg-[#F3F5AE] px-[9px] py-[4px] text-[10.5px] font-bold tracking-[0.05em] text-[#5b5d12] uppercase">
          <CalendarIcon />
          {t(displayPeriod)}
        </span>

        <div className="mt-[20px]">
          <div className="text-grey6 text-[12px] font-medium">
            {t("contaminationPage:recoverableRecyclingRateTitle")}
          </div>
          <div className="mt-[7px] flex items-baseline gap-[6px]">
            <span className="font-signifier text-carrot-green text-[52px] leading-[0.95]">
              {recoverableAmount}
            </span>
            <span className="text-carrot-green text-[14px] font-medium">
              {t("common:percentagePoints")}
            </span>
          </div>
          <div className="text-grey5 mt-[9px] text-[11.5px] leading-[1.45]">
            {t("contaminationPage:recoverableRecyclingRateDescription")}
          </div>
        </div>

        <div className="border-grey2 mt-[20px] flex flex-col gap-[13px] border-t pt-[18px]">
          <div className="flex items-center justify-between gap-[10px]">
            <span className="text-grey6 inline-flex items-center gap-[8px] text-[12.5px] whitespace-nowrap">
              <span className="bg-carrot-green h-[11px] w-[11px] flex-none rounded-[2.5px]" />
              {t(
                "contaminationPage:recoverableRecyclingRateWithoutContamination",
              )}
            </span>
            <span className="font-signifier text-carrot-green text-[26px]">
              {currentPotentialValue}
              <span className="text-[13px]">&nbsp;%</span>
            </span>
          </div>
          <div className="flex items-center justify-between gap-[10px]">
            <span className="text-grey6 inline-flex items-center gap-[8px] text-[12.5px] whitespace-nowrap">
              <span className="h-[11px] w-[11px] flex-none rounded-[2.5px] bg-[#1D1C1C]" />
              {t("contaminationPage:recoverableRecyclingRateActual")}
            </span>
            <span className="font-signifier text-[26px] text-[#1D1C1C]">
              {currentActualValue}
              <span className="text-[13px]">&nbsp;%</span>
            </span>
          </div>
        </div>
      </ViewWithLoader>
    </div>
  );
};
